'use client';
import Accordion from '@repo/ui/src/components/Accordion';
import Button, { ButtonVariant } from '@repo/ui/src/components/Button';
import { useCustomer } from '@hooks/customer';
import { NoteProps, useRecipeNotes } from '@hooks/customer/useRecipeNotes';
import { ModalID, useModal } from '@repo/ui/src/modules/UI';
import { classNames } from '@repo/ui/src/utils/classNames';
import useTranslation from '@repo/ui/src/context/Translation/useTranslation';
import { useCallback, useState } from 'react';
import NoteForm from './_NoteForm';
import { RecipeNote, SegmentRecipeProps } from './_RecipeNote';

export type ID = string | number;

interface Props {
  id: string;
  segmentRecipe: SegmentRecipeProps;
}

const RecipeNotes = ({ id, segmentRecipe }: Props) => {
  const { t } = useTranslation();
  const { data: user } = useCustomer();
  const { push } = useModal(ModalID.login);
  const { data } = useRecipeNotes({ recipeId: id });
  const [showForm, setShowForm] = useState(false);
  const [editingItem, setEditingItem] = useState<NoteProps | null>(null);

  const showEditForm = useCallback((note: NoteProps) => {
    setEditingItem(note);
    setShowForm(true);
  }, []);
  const showAddForm = useCallback(() => {
    setEditingItem(null);
    setShowForm(true);
  }, []);

  return (
    <div className="bg-warm-100 relative p-6 sm:p-20">
      <div className="max-w-body m-auto bg-white grid gap-10 p-6 md:grid-cols-2 md:py-20 md:px-28">
        <div className="max-w-[360px]">
          <h2 className="tra-text-web-heading-2">
            {t('recipeNotesContent.title', undefined, { default: 'My Notes' })}
          </h2>
          <hr className="h-1 w-12 bg-brand-300 block my-6" />
          {!user ? (
            <div>
              <p className="tra-text-web-body-xl">
                {t('recipeNotesContent.unauthedDescription', undefined, {
                  default:
                    'In order to add notes for this recipe, you must log in or create an account.',
                })}
              </p>

              <div className="pt-6 flex flex-col">
                <Button onClick={() => push()}>
                  {t('recipeNotesContent.login', undefined, {
                    default: 'Login',
                  })}
                </Button>
              </div>
            </div>
          ) : (
            <p className="tra-text-web-body-xl">
              {t('recipeNotesContent.authedDescription', undefined, {
                default: 'Add notes during your cook to save for the future.',
              })}
            </p>
          )}
        </div>
        <div className="flex gap-y-6 flex-col">
          <div className="grid grid-areas-overlap">
            <div
              className={classNames(
                showForm ? 'z-10' : 'opacity-0',
                'grid-in-a transition-opacity duration-300',
              )}
            >
              {user && (
                <NoteForm
                  recipeId={id}
                  setShowForm={setShowForm}
                  visible={showForm}
                  note={editingItem}
                  segmentRecipe={segmentRecipe}
                />
              )}
            </div>

            {user && (
              <div
                className={classNames(
                  showForm ? 'opacity-0' : 'z-10',
                  'self-start grid-in-a transition-opacity duration-300 grid gap-y-10',
                )}
              >
                {!data?.length ? (
                  <p>
                    {t('recipeNotesContent.noNoteText', undefined, {
                      default: 'No notes yet, looks like you need to get cooking.',
                    })}
                  </p>
                ) : (
                  <Accordion
                    className="grid gap-y-6"
                    preExpanded={[data?.[0]?.id as ID]}
                    onChange={() => setEditingItem(null)}
                    data-testid="notes-accordion"
                  >
                    {data?.map((note) => (
                      <RecipeNote
                        key={note.id}
                        note={note}
                        recipeId={id}
                        showEditForm={showEditForm}
                        segmentRecipe={segmentRecipe}
                      />
                    ))}
                  </Accordion>
                )}
                <Button variant={ButtonVariant.secondary} onClick={showAddForm}>
                  {t('recipeNotesContent.addButton', undefined, {
                    default: 'Add A Note',
                  })}
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default RecipeNotes;
